import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  MailX,
  Search,
  RefreshCw,
  Loader2,
  Trash2,
  UserPlus,
  AlertTriangle,
  Calendar,
  Mail,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AdminUnsubscribesPage() {
  const [loading, setLoading] = useState(true);
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [processing, setProcessing] = useState(false);

  const perPage = 20;

  const fetchUnsubscribes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await adminAPI.get('/admin/unsubscribes', { params });
      setUnsubscribes(response.data.unsubscribes || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch unsubscribes:', error);
      toast.error('Failed to load unsubscribe list');
    }
    setLoading(false);
  }, [page, searchTerm]);

  useEffect(() => {
    fetchUnsubscribes();
  }, [fetchUnsubscribes]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUnsubscribes();
  };

  const openConfirmDialog = (email, type) => {
    setSelectedEmail(email);
    setActionType(type);
    setConfirmOpen(true);
  };

  const handleResubscribe = async () => {
    if (!selectedEmail) return;
    
    setProcessing(true);
    try {
      await adminAPI.delete(`/admin/unsubscribes/${encodeURIComponent(selectedEmail)}`);
      toast.success(`${selectedEmail} has been resubscribed`);
      setConfirmOpen(false);
      fetchUnsubscribes();
    } catch (error) {
      console.error('Failed to resubscribe:', error);
      toast.error('Failed to resubscribe user');
    }
    setProcessing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-6" data-testid="admin-unsubscribes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Unsubscribe Management</h2>
          <p className="text-slate-500">Manage users who have opted out of email communications</p>
        </div>
        <Button onClick={fetchUnsubscribes} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Unsubscribed</p>
                <p className="text-3xl font-bold text-red-600">{total}</p>
              </div>
              <div className="p-3 rounded-full bg-red-100">
                <MailX className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">This Month</p>
                <p className="text-3xl font-bold text-orange-600">
                  {unsubscribes.filter(u => {
                    const date = new Date(u.unsubscribed_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-orange-100">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Subscribers</p>
                <p className="text-3xl font-bold text-green-600">—</p>
                <p className="text-xs text-slate-400">Total users minus unsubscribed</p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
            {searchTerm && (
              <Button 
                type="button" 
                variant="ghost"
                onClick={() => {
                  setSearchTerm('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Unsubscribes Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MailX className="w-5 h-5 text-red-500" />
            Unsubscribed Emails
          </CardTitle>
          <CardDescription>
            Users who have opted out of receiving emails. You can resubscribe them if needed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : unsubscribes.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No unsubscribed emails found</p>
              {searchTerm && (
                <p className="text-sm text-slate-400 mt-2">Try a different search term</p>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Unsubscribed At</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unsubscribes.map((unsub) => (
                    <TableRow key={unsub.id || unsub.email}>
                      <TableCell>
                        <span className="font-medium">{unsub.email}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-500 text-sm">
                          {formatDate(unsub.unsubscribed_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-500 text-sm">
                          {unsub.reason || 'Not specified'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openConfirmDialog(unsub.email, 'resubscribe')}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Resubscribe
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} entries
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 px-2">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Important Note</h4>
              <p className="text-sm text-amber-700 mt-1">
                Resubscribing a user will allow them to receive emails again. Only do this if the user has explicitly requested to be resubscribed, 
                as sending emails to users who have unsubscribed may violate anti-spam regulations like CAN-SPAM and GDPR.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Resubscribe User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to resubscribe <strong>{selectedEmail}</strong>? 
              This will allow them to receive marketing and notification emails again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResubscribe}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Confirm Resubscribe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
